import { ModalBladeProps } from 'components/layouts/ContentLayout'
import { getFormOptions } from 'utils/validation/greensparkUser'
import { FormProvider, useForm } from 'react-hook-form'
import React, { useEffect, useMemo, useState } from 'react'

import { useAppDispatch, useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import { selectAddEditUserBladeProps } from 'state/ui/selectors'
import { useCreateGsUserMutation, useGetGsUserQuery } from 'services/gsUsers'
import { useGetCustomersQuery } from 'services/customers'
import { Box, Button, Divider, Stack, Typography } from '@mui/material_modern'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import InputV2 from 'components/generic/form/InputV2'
import { useGetOrganizationQuery } from 'services/organizations'
import Select, { SelectItem } from 'components/generic/form/Select'
import { GS_ROLE, GS_ROLE_INFO } from 'utils/constants'
import { setSnackBar } from 'state/ui/index'
import { CreateGsUserApiArg, useUpdateGsUserMutation } from 'gen/rtk-openapi'
import Loading from 'components/generic/Loading'
import Checkbox from 'components/generic/form/Checkbox'
import WarningAmberIcon from '@mui/icons-material_modern/WarningAmber'
import UserPasswordResetModal from 'components/generic/modals/dialog-modal/PasswordResetModal'

export interface AddEditUserBladeProps {
  userId: string
}
export interface UserFormValues {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  allowedYardNameList: string[]
  greensparkRole: GS_ROLE
  orgModeEnabled: boolean
  carrierId: string
  setInitialPasswordManually: boolean
  initialPassword: string
}
const archivedUserStyles = {
  container: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeeba',
    borderRadius: '4px',
    padding: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  icon: {
    color: '#856404',
  },
  text: {
    color: '#856404',
    fontWeight: 'bold',
  },
}

const AddEditUserBlade: React.FC<ModalBladeProps> = ({ onFormStateChange, onHideModal }) => {
  useEffect(() => onFormStateChange(false), [])

  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)

  const { userId } = useAppSelector(selectAddEditUserBladeProps)
  const { appRouteLoadBoard } = useAppSelector(state => state.featureFlags?.variables || {})

  // RTK query hooks
  const { data: orgData } = useGetOrganizationQuery({})
  const {
    data: userData,
    isLoading: isLoadingGetUser,
    error: getUserError,
  } = useGetGsUserQuery({ userId: userId }, { skip: !userId })
  const [createGsUser, { isLoading: isLoadingCreateUser }] = useCreateGsUserMutation()
  const [updateGsUser, { isLoading: isLoadingUpdateUser }] = useUpdateGsUserMutation()

  const { data: carriersData, isLoading: isLoadingCarriersData } = useGetCustomersQuery({
    filter: JSON.stringify({ type: 'CARRIER' }),
  })

  const dispatch = useAppDispatch()

  // Determine if we are updating or creating
  const isUpdate = !!userId
  const userIsArchived = userData?.active === false

  // Form setup
  const methods = useForm<UserFormValues>({
    ...getFormOptions(),
    defaultValues: userData || {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      allowedYardNameList: [],
      greensparkRole: '',
      orgModeEnabled: false,
      carrierId: '',
      setInitialPasswordManually: false,
      initialPassword: '',
    },
  })
  const { handleSubmit, formState, watch, reset } = methods
  const watchRole = watch('greensparkRole')
  const isDriverUser = watchRole === GS_ROLE.DRIVER

  // hydrate form with data when Blade is launched for update
  useEffect(() => {
    if (!userData) return
    reset({
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      phoneNumber: userData.phoneNumber,
      allowedYardNameList: userData.allowedYardNameList,
      greensparkRole: userData.greensparkRole,
      orgModeEnabled: userData.orgModeEnabled,
      carrierId: userData.carrierId,
      setInitialPasswordManually: false,
      initialPassword: '',
    })
  }, [userData])

  useEffect(() => {
    if (watchRole === GS_ROLE.DRIVER || watchRole === GS_ROLE.YARD_ADMIN) {
      methods.setValue('orgModeEnabled', true, {
        shouldDirty: true,
      })
    }
  }, [watchRole])

  const { dirtyFields } = formState
  const isDirty = !!Object.keys(dirtyFields).length
  const isLoading = isLoadingCreateUser || isLoadingGetUser || isLoadingUpdateUser || isLoadingCarriersData

  // notify wrapping ContentLayout component of form state change so that it can prompt user to save changes
  useEffect(() => {
    onFormStateChange(isDirty)
  }, [isDirty])

  const onSubmit = async (formData: UserFormValues) => {
    if (isUpdate) {
      // create partial update payload for PATCH
      const updatePayload = Object.keys(dirtyFields).reduce((acc, field) => {
        acc[field] = formData[field]
        return acc
      }, {})

      try {
        await updateGsUser({
          updateGsUserRequestBody: updatePayload,
          userId,
        }).unwrap()
        handleSuccess()
      } catch (err) {
        handleError(err)
      }
    } else {
      const createArg: CreateGsUserApiArg = {
        createGsUserRequestBody: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          allowedYardNameList: formData.allowedYardNameList,
          userGroupsAssociations: [formData.greensparkRole],
          orgModeEnabled: formData.orgModeEnabled,
          carrierId: formData.carrierId || undefined,
          temporaryPassword: formData.setInitialPasswordManually ? formData.initialPassword : undefined,
        },
      }

      try {
        await createGsUser(createArg).unwrap()
        handleSuccess()
      } catch (err) {
        handleError(err)
      }
    }
  }

  const toggleActiveStatus = async () => {
    const newActiveStatus = userIsArchived ? true : false
    try {
      await updateGsUser({
        updateGsUserRequestBody: { active: newActiveStatus },
        userId,
      }).unwrap()
      handleSuccess()
    } catch (err) {
      handleError(err)
    }
  }

  const handleSuccess = () => {
    dispatch(
      setSnackBar({
        snackbarType: 'success',
        snackbarMessage: `User successfully ${isUpdate ? 'updated' : 'created'}`,
      }),
    )
    onHideModal({ skipBlocker: true })
  }
  const handleError = error => {
    const errorMsg = error?.data?.error || `There was an error ${isUpdate ? 'updating' : 'creating'} the user`
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: errorMsg,
      }),
    )
  }

  // handle fetch errors getting user by id
  useEffect(() => {
    if (getUserError) {
      dispatch(
        setSnackBar({
          snackbarType: 'error',
          snackbarMessage: 'There was an error fetching the user',
        }),
      )
    }
  }, [getUserError])

  const yardOptionList: SelectItem[] = useMemo(() => {
    return (
      orgData?.yards?.map(y => ({
        id: y.name, // both are name because backend wants the name anyway
        name: y.name,
      })) || []
    )
  }, [orgData])

  const carrierOptionList: SelectItem[] = useMemo(() => {
    if (!carriersData?.Items) return []

    return carriersData.Items.map(carrier => {
      return {
        id: carrier.id,
        name: carrier.companyName,
      }
    })
  }, [carriersData])

  const roleOptionList: SelectItem[] = useMemo(() => {
    return Object.entries(GS_ROLE_INFO)
      .filter(([roleName]) => {
        // Exclude PORTAL_USER
        if (roleName === GS_ROLE.PORTAL_USER) return false
        // Exclude DRIVER if appRouteLoadBoard is false
        return !(roleName === GS_ROLE.DRIVER && !appRouteLoadBoard)
      })
      .map(([, roleInfo]) => roleInfo) // Extract role information
  }, [appRouteLoadBoard])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {isLoading ? (
        <Loading localized />
      ) : (
        <Stack height="100vh" justifyContent="flex-start">
          <FormModalHeading
            title={isUpdate ? 'Edit User' : 'Add User'}
            backButtonPosition="right"
            onBack={() => onHideModal()}
          />
          <Divider />

          {/* Information Section */}
          <Box px={4} py={1} flexGrow={1} sx={{ maxWidth: 700, display: 'flex', flexDirection: 'column' }}>
            <FormProvider {...methods}>
              <Stack spacing={1}>
                {!isUpdate && (
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                    Add a new user and we'll kick off their GreenSpark experience with a "Welcome to GreenSpark" email
                    containing login information to get them started.
                  </Typography>
                )}

                <Typography variant="h6">Information</Typography>

                <Stack direction="row" spacing={2}>
                  <Box width={{ xs: '100%', md: '50%' }}>
                    <InputV2
                      name="firstName"
                      label="First Name"
                      data-test="user-first-name"
                      size="small"
                      defaultValue=""
                      error={!!formState.errors?.firstName}
                      helperText={formState.errors?.firstName?.message}
                      disabled={userIsArchived}
                    />
                  </Box>

                  <Box width={{ xs: '100%', md: '50%' }}>
                    <InputV2
                      name="lastName"
                      label="Last Name"
                      data-test="user-last-name"
                      size="small"
                      defaultValue=""
                      error={!!formState.errors?.lastName}
                      helperText={formState.errors?.lastName?.message}
                      disabled={userIsArchived}
                    />
                  </Box>
                </Stack>

                <Box>
                  <InputV2
                    name="email"
                    label="Email"
                    data-test="user-email"
                    size="small"
                    defaultValue=""
                    error={!!formState.errors?.email}
                    helperText={formState.errors?.email?.message}
                    disabled={userIsArchived}
                  />
                </Box>

                {/*button to reset password*/}
                <Box>
                  <Button variant="text" onClick={() => setShowResetPasswordModal(true)} disabled={userIsArchived}>
                    Reset Password
                  </Button>
                </Box>

                <Typography variant="h6">Yard Access</Typography>
                {/* Multi-Select for Yard Access */}
                <Box>
                  <Select
                    error={!!formState.errors?.allowedYardNameList}
                    control={methods.control}
                    name="allowedYardNameList"
                    label="Yards"
                    variant="outlined"
                    size="small"
                    data-testid="allowed-yard-name-list"
                    defaultValue={[]}
                    items={yardOptionList}
                    selectProps={{ multiple: true, sx: { width: '100%' } }}
                    useChips
                    helperText={formState.errors?.allowedYardNameList?.message}
                    disabled={userIsArchived}
                  />
                </Box>

                <Typography variant="h6">Role</Typography>
                {/* Role drop down */}
                <Box>
                  <Select
                    error={!!formState.errors?.greensparkRole}
                    control={methods.control}
                    name="greensparkRole"
                    label="Role"
                    variant="outlined"
                    size="small"
                    data-testid="user-role-select"
                    defaultValue={[]}
                    items={roleOptionList || []}
                    helperText={formState.errors?.greensparkRole?.message}
                    disabled={userIsArchived}
                  />
                </Box>
                {watchRole === GS_ROLE?.DRIVER && (
                  <>
                    <Typography variant="h6">Carrier</Typography>
                    <Box>
                      <Select
                        control={methods.control}
                        name="carrierId"
                        label="Carrier"
                        variant="outlined"
                        size="small"
                        data-testid="user-carrierId-select"
                        defaultValue={[]}
                        items={
                          isUpdate ? [{ name: 'None', id: '' }, ...(carrierOptionList || [])] : carrierOptionList || []
                        }
                        helperText={formState.errors?.carrierId?.message}
                        disabled={userIsArchived || watchRole !== GS_ROLE.DRIVER}
                      />
                    </Box>
                  </>
                )}
                <Box>
                  <Checkbox
                    name="orgModeEnabled"
                    label="Enable Org Mode"
                    control={methods.control}
                    tooltipText="Gives users view access to data across yards. This is necessary for Dispatch and Supplier Portal features."
                    disabled={watchRole === GS_ROLE.DRIVER || watchRole === GS_ROLE.YARD_ADMIN || userIsArchived}
                  />
                  <Typography variant="body2" color="textSecondary">
                    Enable Org Mode to give users access to organization-wide data. Otherwise, users will only see
                    individual yards assigned to them. With Org Mode enabled, 'Org' will appear in the yard selector.
                    Admins and Drivers will have Org Mode enabled by default.
                  </Typography>
                </Box>

                {/* Set initial password manually */}
                {!isUpdate && (
                  <>
                    <Box>
                      <Checkbox
                        name="setInitialPasswordManually"
                        label="Set temporary password manually"
                        control={methods.control}
                        disabled={userIsArchived}
                      />
                    </Box>
                    {watch('setInitialPasswordManually') && (
                      <Box>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                          Whether you set a temporary password manually or not, the user must change their password upon
                          login. Must be at least 8 characters long, contain at least one uppercase letter, one
                          lowercase letter, one number, and one special character.
                        </Typography>
                        <InputV2
                          name="initialPassword"
                          label="Temporary Password "
                          data-test="user-initial-password"
                          size="small"
                          defaultValue=""
                          error={!!formState.errors?.initialPassword}
                          helperText={formState.errors?.initialPassword?.message}
                          disabled={userIsArchived}
                        />
                      </Box>
                    )}
                  </>
                )}
              </Stack>
              <Box px={2} py={2}>
                {userIsArchived && (
                  <Box sx={archivedUserStyles.container}>
                    <WarningAmberIcon sx={archivedUserStyles.icon} />
                    <Typography variant="body1" sx={archivedUserStyles.text}>
                      User is Archived. Restore the user to edit
                    </Typography>
                  </Box>
                )}
              </Box>
            </FormProvider>
          </Box>
          <Divider />

          {/* Footer */}
          <Stack
            direction="row"
            p={2}
            spacing={2}
            sx={{
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Stack direction="row" spacing={2}>
              <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={!isDirty}>
                Save
              </Button>
              <Button variant="outlined" onClick={() => onHideModal()}>
                Cancel
              </Button>
            </Stack>

            {userIsArchived ? (
              <Button variant="outlined" onClick={toggleActiveStatus} disabled={!userIsArchived}>
                Restore
              </Button>
            ) : (
              <Button variant="outlined" color="error" onClick={toggleActiveStatus} disabled={userIsArchived}>
                Archive
              </Button>
            )}
          </Stack>
          <UserPasswordResetModal
            open={showResetPasswordModal}
            onClose={() => setShowResetPasswordModal(false)}
            userId={userId}
          />
        </Stack>
      )}
    </form>
  )
}

export default AddEditUserBlade
